/**
 * <s-carousel> — Native scroll-snap carousel with previous/next buttons.
 *
 * Required child markup:
 *   [data-track]  — The horizontally scrolling list; its children are the slides.
 *   [data-prev]   — Optional button that scrolls back.
 *   [data-next]   — Optional button that scrolls forward.
 *
 * Attributes:
 *   loop      — Endless mode: slides are cloned on both sides (clones are inert and
 *               aria-hidden) so a neighbouring slide always peeks in, and the track
 *               jumps silently back to the real slide when it lands on a clone.
 *               Buttons move one slide at a time and are never disabled.
 *   autoplay  — Milliseconds between automatic advances (loop mode only). Pauses on
 *               hover, focus, hidden tabs and for visitors who prefer reduced motion.
 *
 * Without loop, buttons move one viewport of slides and are disabled at either end.
 * Scrolling, swiping and keyboard scrolling keep working without JavaScript.
 */
class SCarousel extends HTMLElement {
  #track;
  #prev;
  #next;
  #ac;
  #loop = false;
  #realCount = 0;
  #timer = null;
  #paused = false;
  #settleTimer = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-track]');
    if (!this.#track) return;
    this.#prev = this.querySelector('[data-prev]');
    this.#next = this.querySelector('[data-next]');
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#loop = this.hasAttribute('loop') && this.#track.children.length > 1;
    if (this.#loop) this.#setUpLoop();

    this.#prev?.addEventListener('click', () => this.#step(-1), { signal });
    this.#next?.addEventListener('click', () => this.#step(1), { signal });
    this.#track.addEventListener('scroll', () => this.#onScroll(), { signal, passive: true });
    window.addEventListener('resize', () => this.#onResize(), { signal, passive: true });

    if (this.#loop) this.#setUpAutoplay(signal);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stopAutoplay();
    clearTimeout(this.#settleTimer);
  }

  get #slides() {
    return Array.from(this.#track.children);
  }

  #setUpLoop() {
    const originals = this.#slides;
    this.#realCount = originals.length;
    const makeClone = (slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.dataset.clone = '';
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('img[loading="eager"]').forEach((img) => {
        img.setAttribute('loading', 'lazy');
        img.removeAttribute('fetchpriority');
      });
      return clone;
    };
    const before = originals.map(makeClone);
    const after = originals.map(makeClone);
    this.#track.prepend(...before);
    this.#track.append(...after);
    requestAnimationFrame(() => this.#centerOn(this.#realCount, 'instant'));
  }

  #offsetFor(index) {
    const slide = this.#slides[index];
    if (!slide) return 0;
    return slide.offsetLeft - (this.#track.clientWidth - slide.offsetWidth) / 2;
  }

  #centerOn(index, behavior = 'smooth') {
    this.#track.scrollTo({ left: this.#offsetFor(index), behavior });
  }

  #currentIndex() {
    const center = this.#track.scrollLeft + this.#track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #step(direction) {
    if (this.#loop) {
      this.#centerOn(this.#currentIndex() + direction);
      return;
    }
    const track = this.#track;
    const slide = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const slideWidth = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
    const perView = Math.max(1, Math.floor((track.clientWidth + gap) / slideWidth));
    const isRtl = getComputedStyle(track).direction === 'rtl';
    track.scrollBy({ left: direction * perView * slideWidth * (isRtl ? -1 : 1), behavior: 'smooth' });
  }

  #onScroll() {
    this.#sync();
    if (!this.#loop) return;
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#wrapIfOnClone(), 140);
  }

  #wrapIfOnClone() {
    const index = this.#currentIndex();
    const count = this.#realCount;
    if (index < count) this.#centerOn(index + count, 'instant');
    else if (index >= count * 2) this.#centerOn(index - count, 'instant');
  }

  #onResize() {
    if (this.#loop) {
      const index = this.#currentIndex();
      this.#centerOn(index, 'instant');
    }
    this.#sync();
  }

  #setUpAutoplay(signal) {
    const delay = parseInt(this.getAttribute('autoplay'), 10);
    if (!delay || delay < 2000) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const pause = () => (this.#paused = true);
    const resume = () => (this.#paused = false);
    this.addEventListener('pointerenter', pause, { signal });
    this.addEventListener('pointerleave', resume, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', resume, { signal });
    this.#track.addEventListener('touchstart', pause, { signal, passive: true });
    this.#track.addEventListener('touchend', resume, { signal, passive: true });

    this.#timer = setInterval(() => {
      if (this.#paused || document.hidden) return;
      this.#step(1);
    }, delay);
  }

  #stopAutoplay() {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = null;
  }

  #sync() {
    if (this.#loop) return;
    const track = this.#track;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    if (this.#prev) this.#prev.disabled = position <= 2;
    if (this.#next) this.#next.disabled = position >= max - 2;
  }
}

if (!customElements.get('s-carousel')) customElements.define('s-carousel', SCarousel);
